import { getContrastRatio } from '@mui/material/styles';
import type { ChatButtonAccent } from '@/lib/chat-customization';

/** WCAG 2.2 AA — normal text */
export const WCAG_AA_NORMAL = 4.5;

/** WCAG 2.2 AA — large text / UI components on gradients */
export const WCAG_AA_LARGE = 3;

/** Launcher label is ~15px normal text — use AA normal, not large-text / UI 3:1. */
export const LAUNCHER_FOREGROUND_RATIO = WCAG_AA_NORMAL;

const PAPER_BACKGROUND = '#ffffff';
const LIGHT_FOREGROUND = '#ffffff';
const DARK_FOREGROUND = '#000000';

export type BrandSurfaceId = 'gradient' | 'primaryOnPaper' | 'launcherPrimary' | 'launcherSecondaryPuck';

export interface BrandSurfaceFailure {
  surface: BrandSurfaceId;
  message: string;
}

export interface PrimaryAccessibilityResult {
  isFullyCompliant: boolean;
  failures: BrandSurfaceFailure[];
  /** Primary shade that addresses the dominant failure surface, when one exists. */
  suggestedPrimary: string | null;
  /** Which surface `suggestedPrimary` is tuned for; null when no actionable suggestion. */
  suggestionSurface: BrandSurfaceId | null;
}

export interface ResolvedBrandPalette {
  primary: { main: string; contrastText: string };
  secondary: { main: string };
  gradient: { from: string; to: string; foreground: string };
  primaryOnPaper: string;
}

interface FindShadeOptions {
  background: string;
  targetRatio: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = hex.replace('#', '');
  const value =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => char + char)
          .join('')
      : normalized;

  const int = Number.parseInt(value, 16);
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (channel: number) => clamp(Math.round(channel), 0, 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case rn:
        h = ((gn - bn) / delta) % 6;
        break;
      case gn:
        h = (bn - rn) / delta + 2;
        break;
      default:
        h = (rn - gn) / delta + 4;
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rn = 0;
  let gn = 0;
  let bn = 0;

  if (h < 60) {
    rn = c;
    gn = x;
  } else if (h < 120) {
    rn = x;
    gn = c;
  } else if (h < 180) {
    gn = c;
    bn = x;
  } else if (h < 240) {
    gn = x;
    bn = c;
  } else if (h < 300) {
    rn = x;
    bn = c;
  } else {
    rn = c;
    bn = x;
  }

  return {
    r: (rn + m) * 255,
    g: (gn + m) * 255,
    b: (bn + m) * 255,
  };
}

function getRelativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const toLinear = (channel: number) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function pickForegroundForBackground(
  background: string,
  targetRatio: number
): { foreground: string; passes: boolean } {
  const lightRatio = getContrastRatio(LIGHT_FOREGROUND, background);
  const darkRatio = getContrastRatio(DARK_FOREGROUND, background);
  const lightPasses = lightRatio >= targetRatio;
  const darkPasses = darkRatio >= targetRatio;

  if (lightPasses && darkPasses) {
    // Darker brand colours read better with white text even when black barely wins on ratio.
    if (getRelativeLuminance(background) < 0.22) {
      return { foreground: LIGHT_FOREGROUND, passes: true };
    }
    const foreground = lightRatio >= darkRatio ? LIGHT_FOREGROUND : DARK_FOREGROUND;
    return { foreground, passes: true };
  }

  if (lightPasses) {
    return { foreground: LIGHT_FOREGROUND, passes: true };
  }

  if (darkPasses) {
    return { foreground: DARK_FOREGROUND, passes: true };
  }

  const foreground = lightRatio >= darkRatio ? LIGHT_FOREGROUND : DARK_FOREGROUND;
  return { foreground, passes: Math.max(lightRatio, darkRatio) >= targetRatio };
}

/** Returns white or black — whichever meets contrast on the given background. */
export function getContrastForeground(background: string, targetRatio = WCAG_AA_NORMAL): string {
  return pickForegroundForBackground(background, targetRatio).foreground;
}

function foregroundPassesOnBothStops(
  foreground: string,
  from: string,
  to: string,
  targetRatio: number
): boolean {
  return (
    getContrastRatio(foreground, from) >= targetRatio &&
    getContrastRatio(foreground, to) >= targetRatio
  );
}

function nudgeStopTowardCompliant(
  stop: string,
  otherStop: string,
  foreground: string,
  targetRatio: number
): string {
  const { r, g, b } = hexToRgb(stop);
  const { h, s, l } = rgbToHsl(r, g, b);
  const shouldDarken = getRelativeLuminance(stop) >= getRelativeLuminance(otherStop);

  for (let step = 1; step <= 100; step += 1) {
    const progress = step / 100;
    const nextLightness = shouldDarken ? l * (1 - progress) : l + (1 - l) * progress;
    const rgb = hslToRgb(h, s, nextLightness);
    const candidate = rgbToHex(rgb.r, rgb.g, rgb.b);
    if (getContrastRatio(foreground, candidate) >= targetRatio) {
      return candidate;
    }
  }

  return stop;
}

function resolveGradientStops(
  primary: string,
  secondary: string,
  targetRatio: number
): { from: string; to: string; foreground: string } {
  let from = primary;
  let to = secondary;

  for (const foreground of [LIGHT_FOREGROUND, DARK_FOREGROUND]) {
    if (foregroundPassesOnBothStops(foreground, from, to, targetRatio)) {
      return { from, to, foreground };
    }
  }

  let foreground = LIGHT_FOREGROUND;
  if (getContrastRatio(DARK_FOREGROUND, from) > getContrastRatio(LIGHT_FOREGROUND, from)) {
    foreground = DARK_FOREGROUND;
  }

  if (getContrastRatio(foreground, to) < targetRatio) {
    to = nudgeStopTowardCompliant(to, from, foreground, targetRatio);
  }
  if (getContrastRatio(foreground, from) < targetRatio) {
    from = nudgeStopTowardCompliant(from, to, foreground, targetRatio);
  }

  if (!foregroundPassesOnBothStops(foreground, from, to, targetRatio)) {
    foreground = pickForegroundForBackground(from, targetRatio).foreground;
    if (getContrastRatio(foreground, to) < targetRatio) {
      to = nudgeStopTowardCompliant(to, from, foreground, targetRatio);
    }
  }

  return { from, to, foreground };
}

/**
 * Finds the nearest compliant shade by adjusting lightness toward the background.
 */
export function findNearestCompliantShade(color: string, options: FindShadeOptions): string {
  const { background, targetRatio } = options;
  if (getContrastRatio(color, background) >= targetRatio) {
    return color.toLowerCase();
  }

  const { r, g, b } = hexToRgb(color);
  const { h, s, l } = rgbToHsl(r, g, b);
  const shouldDarken = getRelativeLuminance(background) > getRelativeLuminance(color);

  for (let step = 1; step <= 100; step += 1) {
    const progress = step / 100;
    const nextLightness = shouldDarken ? l * (1 - progress) : l + (1 - l) * progress;
    const rgb = hslToRgb(h, s, nextLightness);
    const candidate = rgbToHex(rgb.r, rgb.g, rgb.b);
    if (getContrastRatio(candidate, background) >= targetRatio) {
      return candidate.toLowerCase();
    }
  }

  return color.toLowerCase();
}

function isLauncherCompliant(color: string): boolean {
  const foreground = getContrastForeground(color, LAUNCHER_FOREGROUND_RATIO);
  return getContrastRatio(foreground, color) >= LAUNCHER_FOREGROUND_RATIO;
}

/**
 * Finds the nearest shade where launcher foreground (white/black) meets WCAG AA normal on the background.
 */
export function findNearestLauncherCompliantShade(color: string): string {
  if (isLauncherCompliant(color)) {
    return color.toLowerCase();
  }

  const { r, g, b } = hexToRgb(color);
  const { h, s, l } = rgbToHsl(r, g, b);

  for (let step = 1; step <= 100; step += 1) {
    const progress = step / 100;
    const darkLightness = l * (1 - progress);
    const darkRgb = hslToRgb(h, s, darkLightness);
    const darkCandidate = rgbToHex(darkRgb.r, darkRgb.g, darkRgb.b);
    if (isLauncherCompliant(darkCandidate)) {
      return darkCandidate.toLowerCase();
    }

    const lightLightness = l + (1 - l) * progress;
    const lightRgb = hslToRgb(h, s, lightLightness);
    const lightCandidate = rgbToHex(lightRgb.r, lightRgb.g, lightRgb.b);
    if (isLauncherCompliant(lightCandidate)) {
      return lightCandidate.toLowerCase();
    }
  }

  return color.toLowerCase();
}

function collectAccessibilityFailures(
  primary: string,
  secondary: string,
  chatButtonAccent: ChatButtonAccent,
): BrandSurfaceFailure[] {
  const launcherFailures =
    chatButtonAccent === 'accent_icon'
      ? [checkLauncherOnPrimary(primary), checkLauncherSecondaryPuck(primary, secondary)]
      : [checkLauncherOnPrimary(primary)];

  return [
    checkPrimaryOnPaper(primary),
    ...launcherFailures,
  ].filter((failure): failure is BrandSurfaceFailure => failure !== null);
}

function checkPrimaryOnPaper(primary: string): BrandSurfaceFailure | null {
  // Settings UI warns only below 3:1; the widget still auto-compensates up to 4.5:1 at render.
  if (getContrastRatio(primary, PAPER_BACKGROUND) >= WCAG_AA_LARGE) return null;
  return {
    surface: 'primaryOnPaper',
    message: 'Button labels and links on white backgrounds may be hard to read.',
  };
}

function computeSuggestedPrimary(
  primary: string,
  secondary: string,
  chatButtonAccent: ChatButtonAccent,
  failures: BrandSurfaceFailure[],
): { suggestedPrimary: string | null; suggestionSurface: BrandSurfaceId | null } {
  if (failures.length === 0) {
    return { suggestedPrimary: null, suggestionSurface: null };
  }

  const surfaces = new Set(failures.map((failure) => failure.surface));
  const candidates: Array<{ shade: string; surface: BrandSurfaceId }> = [];

  if (surfaces.has('launcherPrimary')) {
    const launcherShade = findNearestLauncherCompliantShade(primary);
    if (
      launcherShade.toLowerCase() !== primary.toLowerCase() &&
      checkLauncherOnPrimary(launcherShade) === null
    ) {
      candidates.push({ shade: launcherShade, surface: 'launcherPrimary' });
    }
  }

  if (surfaces.has('primaryOnPaper')) {
    const paperShade = findNearestCompliantShade(primary, {
      background: PAPER_BACKGROUND,
      targetRatio: WCAG_AA_NORMAL,
    });
    if (
      paperShade.toLowerCase() !== primary.toLowerCase() &&
      checkPrimaryOnPaper(paperShade) === null
    ) {
      candidates.push({ shade: paperShade, surface: 'primaryOnPaper' });
    }
  }

  if (candidates.length === 0) {
    return { suggestedPrimary: null, suggestionSurface: null };
  }

  let best: { shade: string; surface: BrandSurfaceId } | null = null;
  let bestScore = Number.NEGATIVE_INFINITY;

  for (const candidate of candidates) {
    const reEvalFailures = collectAccessibilityFailures(
      candidate.shade,
      secondary,
      chatButtonAccent,
    );
    const fixesTargetSurface = !reEvalFailures.some(
      (failure) => failure.surface === candidate.surface,
    );
    // Prefer candidates that resolve their target surface; tie-break on fewer remaining failures.
    const score = (fixesTargetSurface ? 1_000 : 0) - reEvalFailures.length;
    if (score > bestScore) {
      best = candidate;
      bestScore = score;
    }
  }

  if (!best || bestScore <= 0) {
    return { suggestedPrimary: null, suggestionSurface: null };
  }

  return { suggestedPrimary: best.shade, suggestionSurface: best.surface };
}

function checkLauncherOnPrimary(primary: string): BrandSurfaceFailure | null {
  const foreground = getContrastForeground(primary, LAUNCHER_FOREGROUND_RATIO);
  if (getContrastRatio(foreground, primary) >= LAUNCHER_FOREGROUND_RATIO) return null;
  return {
    surface: 'launcherPrimary',
    message: 'Text and icons on the chat launcher may be hard to read.',
  };
}

function checkLauncherSecondaryPuck(primary: string, secondary: string): BrandSurfaceFailure | null {
  const puckForeground = getContrastForeground(secondary, WCAG_AA_LARGE);
  const glyphContrast = getContrastRatio(puckForeground, secondary);
  const puckContrast = getContrastRatio(secondary, primary);

  if (glyphContrast >= WCAG_AA_LARGE && puckContrast >= WCAG_AA_LARGE) return null;

  if (glyphContrast < WCAG_AA_LARGE) {
    return {
      surface: 'launcherSecondaryPuck',
      message: 'The icon accent on the chat launcher may be hard to read.',
    };
  }

  return {
    surface: 'launcherSecondaryPuck',
    message: 'The secondary accent on the chat launcher may be hard to distinguish from the primary colour.',
  };
}

/**
 * Evaluates whether the institution-selected primary colour is compliant across widget surfaces.
 */
export function evaluatePrimaryColorAccessibility(
  primary: string,
  secondary: string,
  chatButtonAccent: ChatButtonAccent = 'primary_only',
): PrimaryAccessibilityResult {
  let failures = collectAccessibilityFailures(primary, secondary, chatButtonAccent);

  const { suggestedPrimary, suggestionSurface } = computeSuggestedPrimary(
    primary,
    secondary,
    chatButtonAccent,
    failures,
  );

  // Paper warnings are only shown when a "Use #…" shade is available.
  if (failures.some((failure) => failure.surface === 'primaryOnPaper') && !suggestedPrimary) {
    failures = failures.filter((failure) => failure.surface !== 'primaryOnPaper');
  }

  return {
    isFullyCompliant: failures.length === 0,
    failures,
    suggestedPrimary,
    suggestionSurface,
  };
}

/**
 * Resolves an always-accessible palette for widget rendering.
 */
export function resolveBrandPalette(primary: string, secondary: string): ResolvedBrandPalette {
  const contrastText = pickForegroundForBackground(primary, WCAG_AA_NORMAL).foreground;

  const primaryOnPaper =
    getContrastRatio(primary, PAPER_BACKGROUND) >= WCAG_AA_NORMAL
      ? primary
      : findNearestCompliantShade(primary, {
          background: PAPER_BACKGROUND,
          targetRatio: WCAG_AA_NORMAL,
        });

  const gradient = resolveGradientStops(primary, secondary, WCAG_AA_LARGE);

  return {
    primary: { main: primary, contrastText },
    secondary: { main: secondary },
    gradient,
    primaryOnPaper,
  };
}
